import React, { useCallback, useEffect, useState } from 'react';
import { Card, Alert, Button, Descriptions, Space, Tag, Typography, Statistic, Row, Col, message, Steps } from 'antd';
import { ApiOutlined, ReloadOutlined, SendOutlined, CheckCircleOutlined, CloseCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { getDigilockerWebhookHealth, sendDigilockerWebhookTest } from '../api';

const { Text, Paragraph } = Typography;

const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');

const OUTCOME_LABEL = {
    bad_signature: 'Signature mismatch',
    no_secret: 'No webhook secret saved',
    bad_payload: 'Unreadable payload',
};

/**
 * API Configuration → Sandbox → DigiLocker Webhook.
 * Shows the URL to paste into Sandbox Console, whether deliveries are arriving
 * and verifying, how many billed calls a verification is costing, and a
 * one-click signed self-test through the public URL.
 */
const DigilockerWebhookCard = ({ canEdit }) => {
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setHealth(await getDigilockerWebhookHealth());
        } catch (e) {
            message.error('Failed to load DigiLocker webhook status');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const runTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const r = await sendDigilockerWebhookTest();
            setTestResult(r);
            if (r.ok) message.success(`Webhook reachable and signature verified (${r.ms} ms)`);
            else message.error(r.message || `Test failed (HTTP ${r.status || 'no response'})`);
            load();
        } catch (e) {
            const msg = e.response?.data?.message || e.message;
            setTestResult({ ok: false, message: msg });
            message.error(msg);
        } finally {
            setTesting(false);
        }
    };

    const h = health || {};
    const stats = h.last7Days || {};
    const ready = h.secretConfigured && h.httpsReady;

    const statusTag = () => {
        if (!h.httpsReady) return <Tag color="error" icon={<CloseCircleOutlined />}>Webhook Base URL must be HTTPS</Tag>;
        if (h.returnReachable === false) return <Tag color="error" icon={<CloseCircleOutlined />}>Webhook Base URL does not reach the CRM backend</Tag>;
        if (!h.secretConfigured) return <Tag color="warning" icon={<WarningOutlined />}>Secret not saved — all webhooks rejected</Tag>;
        if (h.lastValidAt) return <Tag color="success" icon={<CheckCircleOutlined />}>Receiving webhooks</Tag>;
        if (h.lastTestAt) return <Tag color="processing">Self-test passed — waiting for first Sandbox event</Tag>;
        return <Tag>Not tested yet</Tag>;
    };

    return (
        <Card
            style={{ marginTop: 24 }}
            title={<Space><ApiOutlined />DigiLocker Webhook</Space>}
            extra={<Button icon={<ReloadOutlined />} onClick={load} loading={loading} size="small">Refresh</Button>}
        >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>{statusTag()}</div>
                {h.returnReachable === false && (
                    <Alert
                        type="error"
                        showIcon
                        message="DigiLocker will land on a blank page and Sandbox webhooks will be lost"
                        description={`${h.returnProblem || 'The URL is not served by the CRM backend.'} Fix it in the Webhook tab (Webhook Base URL), then update the URL in Sandbox Console.`}
                    />
                )}

                <Descriptions bordered size="small" column={1}>
                    <Descriptions.Item label="Webhook URL (paste in Sandbox Console)">
                        <Text copyable={{ text: h.webhookUrl }} code>{h.webhookUrl || '—'}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Redirect URL (set automatically)">
                        <Text type="secondary" code>{h.returnUrl ? `${h.returnUrl}?ref=…` : '—'}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Last valid webhook">{fmt(h.lastValidAt)}</Descriptions.Item>
                    <Descriptions.Item label="Last rejected delivery">
                        {h.lastRejected
                            ? <Text type="danger">{fmt(h.lastRejected.at)} — {OUTCOME_LABEL[h.lastRejected.outcome] || h.lastRejected.outcome}</Text>
                            : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Last self-test">{fmt(h.lastTestAt)}</Descriptions.Item>
                </Descriptions>

                <Row gutter={16}>
                    <Col xs={12} md={6}><Statistic title="Sessions (7d)" value={stats.sessions || 0} /></Col>
                    <Col xs={12} md={6}><Statistic title="Sandbox calls (7d)" value={stats.sandboxCalls || 0} /></Col>
                    <Col xs={12} md={6}><Statistic title="Avg calls / session" value={stats.avgCallsPerSession || 0} precision={1} /></Col>
                    <Col xs={12} md={6}>
                        <Statistic
                            title="Stuck fetches (7d)"
                            value={stats.stuckFetches || 0}
                            valueStyle={stats.stuckFetches ? { color: '#cf1322' } : undefined}
                        />
                    </Col>
                </Row>
                {stats.settledBy && Object.keys(stats.settledBy).length > 0 && (
                    <Text type="secondary">
                        Settled by — webhook: {stats.settledBy.webhook || 0}, redirect: {stats.settledBy.return || 0}, manual check: {stats.settledBy.status_check || 0}.
                        {' '}Mostly “redirect” means Sandbox webhooks are not arriving.
                    </Text>
                )}

                <Space wrap>
                    <Button type="primary" icon={<SendOutlined />} onClick={runTest} loading={testing} disabled={!canEdit || !ready}>
                        Send signed test event
                    </Button>
                    {!ready && <Text type="secondary">Save an HTTPS Webhook Base URL and the Webhook Secret first.</Text>}
                </Space>
                {testResult && (
                    <Alert
                        type={testResult.ok ? 'success' : 'error'}
                        showIcon
                        message={testResult.ok
                            ? `Delivered to ${testResult.webhookUrl} and signature verified in ${testResult.ms} ms.`
                            : `Test failed${testResult.status ? ` (HTTP ${testResult.status})` : ''}: ${testResult.message || 'no response'}`}
                    />
                )}

                <Alert
                    type="info"
                    showIcon
                    message="Set up in Sandbox Console"
                    description={(
                        <Steps
                            direction="vertical"
                            size="small"
                            current={-1}
                            items={[
                                { title: 'Sandbox Console → Settings (top-right) → Webhooks' },
                                { title: 'Webhook URL', description: 'Paste the Webhook URL above.' },
                                { title: 'Secret Key', description: 'Generate a long random secret, paste it there AND in “Webhook Secret” above, then save both.' },
                                { title: 'Category / API', description: 'Choose KYC → DigiLocker (session events). Optionally add a failure-alert email.' },
                                { title: 'Save, then press “Send signed test event” here.' },
                            ]}
                        />
                    )}
                />
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    Unsigned or wrongly signed webhooks are rejected (HTTP 401). If webhooks stop arriving, verifications
                    still complete through the redirect callback — at the cost of one extra Sandbox call each.
                </Paragraph>
            </Space>
        </Card>
    );
};

export default DigilockerWebhookCard;
